import { useGetList } from "ra-core";
import { useMemo } from "react";

import { useConfigurationContext } from "../root/ConfigurationContext";
import {
  computeLeadScore,
  useOpenDealContactIds,
} from "../contacts/leadScore";
import type { Contact, Sale, Tag } from "../types";

export type ReportFieldType = "number" | "text" | "date" | "enum" | "owner";

export interface ReportFieldDef {
  key: string;
  label: string;
  type: ReportFieldType;
  choices?: { id: string; name: string }[];
  /**
   * Computed/relationship fields resolve through this instead of reading
   * record[key] - lets lists, reports, and workflow conditions filter on
   * lead score, touch channels, campaign engagement, etc.
   */
  getValue?: (record: any) => unknown;
}

const CHANNELS = [
  { id: "organic", name: "Organic search" },
  { id: "paid", name: "Paid" },
  { id: "social", name: "Social" },
  { id: "email", name: "Email" },
  { id: "direct", name: "Direct" },
  { id: "referral", name: "Referral" },
];

const sortedTouches = (record: Contact) =>
  [...(record.touches ?? [])].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

/** The filterable/groupable field catalog for a report object. */
export const useReportFieldDefs = (
  object: "deals" | "contacts",
): ReportFieldDef[] => {
  const {
    dealStages,
    dealCategories,
    noteStatuses,
    lifecycleStages,
    closeReasons,
    customFields,
    scoringRules,
    campaigns,
  } = useConfigurationContext();
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });
  const { data: tags = [] } = useGetList<Tag>("tags", {
    pagination: { page: 1, perPage: 100 },
  });
  const openDealIds = useOpenDealContactIds();

  return useMemo(() => {
    const owners = sales.map((s) => ({
      id: String(s.id),
      name: `${s.first_name} ${s.last_name}`,
    }));
    const custom = (customFields ?? [])
      .filter((f) => f.object === object && f.key)
      .map((f) => ({
        key: `custom_${f.key}`,
        label: f.label,
        type: (f.type === "select" ? "text" : f.type) as ReportFieldType,
        // Values live nested under custom_fields, not a flat custom_<key>
        // property - record[key] alone wouldn't find them.
        getValue: (record: any) => record?.custom_fields?.[f.key ?? ""],
      }));

    if (object === "deals") {
      return [
        { key: "stage", label: "Stage", type: "enum" as const, choices: dealStages.map((s) => ({ id: s.value, name: s.label })) },
        { key: "category", label: "Category", type: "enum" as const, choices: dealCategories.map((c) => ({ id: c.value, name: c.label })) },
        { key: "sales_id", label: "Owner", type: "owner" as const, choices: owners },
        { key: "amount", label: "Amount", type: "number" as const },
        { key: "created_at", label: "Created", type: "date" as const },
        { key: "closed_at", label: "Closed", type: "date" as const },
        {
          key: "close_reason",
          label: "Lost reason",
          type: "enum" as const,
          choices: closeReasons.map((r) => ({ id: r.value, name: r.label })),
        },
        { key: "expected_closing_date", label: "Expected close", type: "date" as const },
        { key: "name", label: "Deal name", type: "text" as const },
        ...custom,
      ];
    }

    const gradeOf = (record: Contact) =>
      computeLeadScore(record, scoringRules ?? [], openDealIds);

    const engagementFields: ReportFieldDef[] = (campaigns ?? [])
      .filter((c) => c.status === "sent" && c.analytics)
      .map((c) => ({
        key: `campaign_${c.id}`,
        label: `Campaign: ${c.name}`,
        type: "enum" as const,
        choices: [
          { id: "clicked", name: "Clicked" },
          { id: "opened_no_click", name: "Opened, didn't click" },
          { id: "not_opened", name: "Didn't open" },
          { id: "not_sent", name: "Wasn't sent it" },
        ],
        getValue: (record: Contact) => {
          const row = c.analytics!.engagement.find(
            (e) => String(e.contactId) === String(record.id),
          );
          if (!row) return "not_sent";
          if (row.clickedLinks.length > 0) return "clicked";
          return row.opened ? "opened_no_click" : "not_opened";
        },
      }));

    return [
      { key: "status", label: "Status", type: "enum" as const, choices: noteStatuses.map((s) => ({ id: s.value, name: s.label })) },
      {
        key: "lifecycle_stage",
        label: "Lifecycle stage",
        type: "enum" as const,
        choices: lifecycleStages.map((s) => ({ id: s.value, name: s.label })),
      },
      { key: "sales_id", label: "Owner", type: "owner" as const, choices: owners },
      {
        key: "lead_score",
        label: "Lead score",
        type: "number" as const,
        getValue: (r: Contact) => gradeOf(r).score,
      },
      {
        key: "lead_grade",
        label: "Lead grade",
        type: "enum" as const,
        choices: ["A", "B", "C", "D"].map((g) => ({ id: g, name: g })),
        getValue: (r: Contact) => gradeOf(r).grade,
      },
      {
        key: "first_touch_channel",
        label: "First touch channel",
        type: "enum" as const,
        choices: CHANNELS,
        getValue: (r: Contact) => sortedTouches(r)[0]?.channel,
      },
      {
        key: "last_touch_channel",
        label: "Last touch channel",
        type: "enum" as const,
        choices: CHANNELS,
        getValue: (r: Contact) => sortedTouches(r).at(-1)?.channel,
      },
      {
        key: "has_newsletter",
        label: "Newsletter",
        type: "enum" as const,
        choices: [
          { id: "yes", name: "Subscribed" },
          { id: "no", name: "Not subscribed" },
        ],
        getValue: (r: Contact) => (r.has_newsletter ? "yes" : "no"),
      },
      {
        key: "tag_names",
        label: "Tags",
        type: "text" as const,
        getValue: (r: Contact) =>
          (r.tags ?? [])
            .map((id) => tags.find((t) => t.id === id)?.name)
            .filter(Boolean)
            .join(", "),
      },
      { key: "first_seen", label: "First seen", type: "date" as const },
      { key: "last_seen", label: "Last seen", type: "date" as const },
      { key: "title", label: "Title", type: "text" as const },
      { key: "last_name", label: "Last name", type: "text" as const },
      ...engagementFields,
      ...custom,
    ];
  }, [
    object,
    customFields,
    dealStages,
    dealCategories,
    noteStatuses,
    lifecycleStages,
    closeReasons,
    scoringRules,
    campaigns,
    sales,
    tags,
    openDealIds,
  ]);
};
